<template>
	<div v-if="hrDocument" style="max-width: 960px; padding: 12px">
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<v-alert v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE) && hrDocument.newVersionAvailable" :value="true" class="text-xs-left mb-4" color="info" icon="mdi-cloud-download" outline>
			There is a new version of this policy available for use. <a style="text-decoration: underline" @click="onShowUpdateAvailableModal">Click here to view</a>.
		</v-alert>

		<div v-if="!isTemplate">
			<v-alert v-if="hrDocument.isRelevantToEmployee && !hrDocument.agreedData.agreed" :value="true" class="text-xs-left mb-4" icon="mdi-alert-circle-outline" color="warning">
				It is required that you acknowledge the contents of this policy. <a style="color: #fff; text-decoration: underline" @click.stop="scrollToAcknowledgement">This can be done at the bottom of the policy</a>
			</v-alert>
			<v-alert v-else-if="hrDocument.agreedData.agreed" :value="true" class="text-xs-left mb-4" icon="mdi-checkbox-marked-outline" color="success" dismissible>
				You acknowledged the contents of this policy on {{ hrDocument.agreedData.agreementDateUtc | formatDateTime }}
			</v-alert>
		</div>

		<hr-document-settings-dialog
			v-if="settingsDialog"
			:dialog="settingsDialog"
			:relevantParties="{ ...hrDocument.relevantParties }"
			:acknowledgementTextOption="hrDocument.acknowledgementTextOption"
			:currentCategory="hrDocument.category"
			@onCancel="settingsDialog = false"
			@onSave="onSaveSettings"
			:isSaving="isSavingSettings"
		/>

		<v-card>
			<v-toolbar v-if="!hideFooter" card prominent class="flexible-toolbar" :extended="!!hrDocument.category">
				<v-toolbar-title class="body-2 grey--text"></v-toolbar-title>
				<div class="chip-wrap">
					<!-- <v-tooltip bottom v-if="hrDocument.category">
						<template v-slot:activator="{ on }">
							<v-chip v-on="on" label><v-icon class="mr-2" small>mdi-folder-multiple-outline</v-icon>{{ hrDocument.category.name }}</v-chip>
						</template>

						<span>Category: {{ hrDocument.category.name }}</span>
					</v-tooltip> -->

					<v-chip v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE) && hrDocument.lastReviewedDateUTC" outlined>
						<v-icon small class="mr-1">mdi-file-search-outline</v-icon>
						<span class="mr-1">Last Reviewed by</span><a href="#" @click.stop.prevent="showReviewDetails = true">{{ mostRecentReview.reviewedBy.fullName }} on {{ hrDocument.lastReviewedDateUTC | formatDate }}</a>
					</v-chip>
				</div>
				<v-spacer></v-spacer>

				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="printClick">
							<v-icon>print</v-icon>
						</v-btn>
					</template>
					<span>Print Policy</span>
				</v-tooltip>

				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn v-if="hrDocument.history !== null && hrDocument.history.length > 0" icon v-on="on" @click="viewHistoryClick">
							<v-icon>history</v-icon>
						</v-btn>
					</template>
					<span>View Version History</span>
				</v-tooltip>

				<v-tooltip v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE)" bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="viewCompliantEmployeesClick">
							<v-icon>mdi-account-multiple-check</v-icon>
						</v-btn>
					</template>
					<span>View Employee's who have agreed to this Policy</span>
				</v-tooltip>

				<v-tooltip v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE)" bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="settingsDialog = true">
							<v-icon>settings</v-icon>
						</v-btn>
					</template>
					<span>Configure Policy Settings</span>
				</v-tooltip>

				<v-btn v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE) && !isLocked" small flat @click="onEditOrReplace(hrDocument)">
					<v-icon dark class="mr-2">edit</v-icon>
					Edit Policy
				</v-btn>

				<v-menu bottom left v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE)">
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on"><v-icon>mdi-dots-horizontal</v-icon> </v-btn>
					</template>
					<v-list>
						<v-list-tile @click="reviewDialog = true">
							<v-list-tile-title>Mark as Reviewed</v-list-tile-title>
						</v-list-tile>
					</v-list>
				</v-menu>

				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-chip color="indigo" dark v-on="on"> V.{{ hrDocument.versionNo }} </v-chip>
					</template>
					<span>Document Version: {{ hrDocument.versionNo }}</span>
				</v-tooltip>

				<template v-slot:extension v-if="!!hrDocument.category">
					<v-spacer></v-spacer>
					<v-tooltip bottom v-if="hrDocument.category">
						<template v-slot:activator="{ on }">
							<v-chip v-on="on" label><v-icon class="mr-2" small>mdi-folder-multiple-outline</v-icon>{{ hrDocument.category.name }}</v-chip>
						</template>

						<span>Policy Category: {{ hrDocument.category.name }}</span>
					</v-tooltip>
				</template>
			</v-toolbar>
			<v-card-text class="hr-document">
				<div v-if="!isPdf" class="header">
					<v-layout row wrap>
						<v-flex md3 class="text-xs-left">
							<div class="logo">
								<img :src="loggedInTenant.logoUrl" />
							</div>
						</v-flex>

						<v-flex md6 class="mt-3" style="text-align: center">
							<h1>{{ hrDocument.name }}</h1>
						</v-flex>

						<v-flex md3 class="text-xs-left mt-3">
							<div class="header-right info-text">
								<div v-if="!isTemplate">
									<div>Version: {{ hrDocument.versionNo }}</div>
									<div v-if="hrDocument.lastUpdatedDateUTC" class="info-text">Last Updated: {{ hrDocument.lastUpdatedDateUTC | formatDate }}</div>
									<div v-else class="info-text">Created: {{ hrDocument.createdDateUTC | formatDate }}</div>
								</div>
							</div>
						</v-flex>
					</v-layout>
				</div>

				<div class="content">
					<div v-if="!isPdf" v-html="populatePlaceholders(hrDocument.content)"></div>

					<div v-else>
						<div v-if="isLoadingPdf" class="text-xs-center">
							<v-progress-circular indeterminate color="primary"></v-progress-circular>
						</div>
						<div v-else>
							<vue-pdf-embed :source="pdfDocSrc" style="display: inline-block; width: 100%"></vue-pdf-embed>
						</div>
					</div>
				</div>
				<div class="footer"></div>
			</v-card-text>
		</v-card>

		<hr-document-agreement-dialog
			v-if="!isTemplate && !hrDocument.agreedData.agreed"
			id="policy-acknowledgement"
			class="mt-4 noPrint"
			:is-agreeing="isAgreeing"
			:acknowledgement-text-option="hrDocument.acknowledgementTextOption"
			@onAgree="onAgree"
		/>

		<hr-document-update-available-modal
			v-if="showHrDocumentUpdateAvailableModal"
			:is-admin="isAdmin"
			:is-saving="isUpdatingToLatest"
			:dialog="showHrDocumentUpdateAvailableModal"
			:logged-in-tenant="loggedInTenant"
			:template="template"
			@onUpdate="onUpdateHrDocument"
			@closeModal="showHrDocumentUpdateAvailableModal = !showHrDocumentUpdateAvailableModal"
		/>

		<review-hr-document-dialog v-if="!isTemplate && reviewDialog" @onCancel="reviewDialog = false" :id="hrDocument.id" @onReviewed="onReviewed" />

		<employee-agreements-dialog v-if="!isTemplate && agreementsDialog" :dialog="agreementsDialog" :agreements="hrDocument.agreements" @onCancel="onCancelAgreementsDialog" />

		<previous-versions-dialog v-if="showPreviousVersionsDialog" :dialog="showPreviousVersionsDialog" :hrDocument="hrDocument" @onCancel="showPreviousVersionsDialog = false" />

		<hrDocumentReviewDetailDialog v-if="mostRecentReview" v-model="showReviewDetails" :review="mostRecentReview" />

		<replace-hr-document-dialog :dialog="showReplaceDialog" :isSaving="isReplacing" :initialName="hrDocument.name" :error="replaceError" @cancel="showReplaceDialog = false" @done="onReplacePdfDone" />
	</div>
</template>

<script>
	import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
	import hrDocumentAgreementDialog from '@/components/hrDocuments/HrDocumentAgreementDialog';
	import employeeAgreementsDialog from '@/components/hrDocuments/EmployeeAgreementsDialog';
	import hrDocumentMixin from '@/mixins/hrDocumentMixin';
	import hrDocumentSettingsDialog from '@/components/hrDocuments/HrDocumentSettingsDialog';
	import reviewHrDocumentDialog from '@/components/hrDocuments/ReviewHrDocumentDialog';
	import hrDocumentUpdateAvailableModal from '@/components/hrDocuments/HrDocumentUpdateAvailableModal';
	import previousVersionsDialog from '@/components/hrDocuments/PreviousVersionsDialog';
	import hrDocumentReviewDetailDialog from '@/components/hrDocuments/HrDocumentReviewDetailDialog';
	import replaceHrDocumentDialog from '@/components/hrDocuments/ReplaceHrDocumentDialog';

	export default {
		name: 'HrDocument',
		props: {
			hrDocument: {
				required: true,
				type: Object
			},
			loggedInTenant: {
				required: true,
				type: Object
			},
			hideFooter: {
				required: false,
				type: Boolean
			},
			isTemplate: {
				required: false,
				type: Boolean
			},
			isAdmin: {
				type: Boolean,
				required: true
			},
			isAgreeing: {
				type: Boolean,
				default: false
			},
			loadUpdate: {
				type: Boolean,
				default: false
			}
		},
		components: {
			VuePdfEmbed,
			hrDocumentAgreementDialog,
			employeeAgreementsDialog,
			hrDocumentSettingsDialog,
			hrDocumentUpdateAvailableModal,
			previousVersionsDialog,
			reviewHrDocumentDialog,
			hrDocumentReviewDetailDialog,
			replaceHrDocumentDialog
		},
		data() {
			return {
				pdfDocSrc: null,
				pdfDocNumPages: undefined,
				isLoadingPdf: false,
				agreementsDialog: false,
				settingsDialog: false,
				reviewDialog: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				isSavingSettings: false,
				showHrDocumentUpdateAvailableModal: false,
				isUpdatingToLatest: false,
				showPreviousVersionsDialog: false,
				showReviewDetails: false,
				isReplacing: false,
				replaceError: null,
				showReplaceDialog: false
			};
		},
		created() {
			if (this.hrDocument) {
				this.$store.dispatch('setPageTitle', this.hrDocument.name);
			} else {
				this.$store.dispatch('setPageTitle', 'Company Policy');
			}

			if (this.hrDocument && this.hrDocument.contentType === 1) {
				this.loadFile();
			}
			if (this.loadUpdate) {
				this.showHrDocumentUpdateAvailableModal = true;
			}
		},
		mounted() {
			window.scrollTo(0, 0);
		},
		computed: {
			isPdf() {
				return this.hrDocument.contentType == 1;
			},
			template() {
				if (!this.hrDocument.templateId) return null;

				return this.$store.getters['marketplace/getHrDocumentTemplate'](this.hrDocument.templateId);
			},
			mostRecentReview() {
				if (!this.hrDocument.reviews || this.hrDocument.reviews.length === 0) return null;

				return [...this.hrDocument.reviews].sort((a, b) => new Date(b.reviewDateUTC) - new Date(a.reviewDateUTC))[0];
			},
			isLocked() {
				if (!this.hrDocument) return false;

				return this.hrDocument.isLocked;
			}
		},
		methods: {
			loadFile() {
				this.isLoadingPdf = true;

				const fetchDocument = this.isTemplate ? this.$store.dispatch('marketplace/getHrDocumentTemplateFile', this.hrDocument.id) : this.$store.dispatch('hrDocuments/getHrDocumentFile', this.hrDocument.id);

				fetchDocument.then(response => {
					const documentLoadingTask = VuePdfEmbed.getDocument(window.URL.createObjectURL(new Blob([response.data])));

					documentLoadingTask.promise.then(pdf => {
						this.pdfDocSrc = pdf;
						this.isLoadingPdf = false;
						this.pdfDocNumPages = pdf._pdfInfo.numPages;
					});
				});
			},
			viewHistoryClick() {
				this.showPreviousVersionsDialog = true;
			},
			viewCompliantEmployeesClick() {
				this.agreementsDialog = true;
			},
			onCancelAgreementsDialog() {
				this.agreementsDialog = false;
			},
			printClick() {
				if (this.isPdf) {
					this.printPdf();
				} else {
					window.print();
				}
			},
			async printPdf() {
				if (!this.pdfDocSrc) {
					console.error('PDF document is not loaded yet.');
					return;
				}

				try {
					// Get the PDF as a Blob
					const data = await this.pdfDocSrc.getData();
					const blob = new Blob([data], { type: 'application/pdf' });

					// Create a Blob URL and open it in a new window
					const pdfUrl = URL.createObjectURL(blob);
					const newWindow = window.open(pdfUrl, '_blank');

					if (newWindow) {
						newWindow.onload = () => {
							newWindow.print();
							URL.revokeObjectURL(pdfUrl);
						};
					} else {
						console.error('Failed to open new window for PDF printing.');
					}
				} catch (error) {
					console.error('Failed to print the PDF:', error);
				}
			},
			onAgree(agreementData) {
				this.$emit('onAgree', agreementData);
			},
			scrollToAcknowledgement() {
				const element = document.querySelector('#policy-acknowledgement');

				// smooth scroll to element and align it at the bottom
				element.scrollIntoView({ behavior: 'smooth', block: 'end' });
			},
			onSaveSettings(settings) {
				this.isSavingSettings = true;

				const data = {
					id: this.hrDocument.id,
					mandatoryForAll: settings.mandatoryForAll,
					acknowledgementTextOption: settings.acknowledgementTextOption,
					category: settings.category
				};

				this.$store
					.dispatch('hrDocuments/updateSettings', data)
					.then(response => {
						this.settingsDialog = false;
						this.$emit('onUpdate', 'Policy settings have been updated');
					})
					.catch(err => {
						this.showSnackbar('Policy settings failed to update: ' + error.message, true);
					})
					.finally(() => {
						this.isSavingSettings = false;
					});
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			onEditOrReplace(hrDocument) {
				if (hrDocument.contentType === 0) {
					// HTML
					this.$router.push(`/hrDocuments/${hrDocument.id}/edit`);
				} else {
					// PDF
					this.showReplaceDialog = true;
				}
			},
			onUpdateHrDocument() {
				this.isUpdatingToLatest = true;

				this.$store
					.dispatch('hrDocuments/updateToLatestTemplate', this.hrDocument.id)
					.then(response => {
						this.showHrDocumentUpdateAvailableModal = false;
						this.hrDocument.newVersionAvailable = false;

						this.loadFile();

						this.showSnackbar('Policy Updated');
					})
					.catch(err => {
						this.showSnackbar('Policy failed to update: ' + error.message, true);
					})
					.finally(() => {
						this.isUpdatingToLatest = false;
					});
			},
			onShowUpdateAvailableModal() {
				this.showHrDocumentUpdateAvailableModal = true;
			},
			onReviewed() {
				this.reviewDialog = false;
				this.$emit('onUpdate');
			},
			async onReplacePdfDone(formData) {
				this.isReplacing = true;
				this.replaceError = null;
				try {
					await this.$store.dispatch('hrDocuments/updatePdf', {
						id: this.hrDocument.id,
						formData
					});
					this.showSnackbar('Policy replaced successfully');
					this.showReplaceDialog = false;
					this.$emit('onUpdate');
					if (this.isPdf) this.loadFile();
				} catch (e) {
					this.replaceError = e.response.data || e.message || 'Failed to replace policy.';
				} finally {
					this.isReplacing = false;
				}
			}
		},
		mixins: [hrDocumentMixin]
	};
</script>

<style scoped>
	.header {
		min-height: 100px;
		margin-bottom: 25px;
	}
	.header .header-right {
		text-align: right;
	}
	.header h1 {
		font-size: 1.7em;
	}
	.content {
		text-align: left;
	}
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
		font-style: italic;
	}
	.logo img {
		max-height: 100px;
		max-width: 220px;
	}
	.chip-wrap {
		display: flex;
		flex-wrap: wrap;
	}
	/* .flexible-toolbar {
		flex-wrap: wrap;
		align-items: flex-start;
		min-height: 64px;
		padding-top: 12px;
		padding-bottom: 12px;
	} */
	.flexible-toolbar >>> .v-toolbar__extension {
		margin-top: -10px;
	}
</style>
